// Record a payment the patient has already handed over, against one claim.
// Opened from the ledger detail panel.
//
// This RECORDS money already collected. It does not take a payment — there is no
// card, no Stripe, no charge. The copy says so, because "Record payment" on a
// screen with an amount box is otherwise read as a terminal.
//
// Principle 10, same shape as WriteoffModal: mounted only when opened, empty on
// open, focus on the amount and never on the confirm button, and the button names
// the amount it will record.

import React, { useEffect, useRef, useState } from "react";
import "./PostingModals.css";
import { formatCurrency } from "../../utils/formatCurrency";
import { DATE_INPUT_MIN } from "../../utils/dateInputBounds";

const API_URL = process.env.REACT_APP_API_URL || "";

// patient_payment_service._VALID_METHODS.
const METHODS = [
  { key: "cash", label: "Cash" },
  { key: "card", label: "Card" },
  { key: "check", label: "Check" },
];

function today() {
  // LOCAL date. `toISOString()` is UTC, so after 17:00 Pacific it returns tomorrow —
  // and this is the date on a money record. Worse, the future-date guard below
  // compared against the SAME UTC value, so the wrong default validated cleanly.
  // 'en-CA' is the locale whose short date IS ISO, so this needs no hand-rolled
  // padding and no timezone maths.
  return new Date().toLocaleDateString("en-CA");
}

export default function PatientPaymentModal({
  practiceId,
  claim,
  onClose,
  onPosted,
}) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("");
  const [paymentDate, setPaymentDate] = useState(today());
  const [reference, setReference] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);
  const amountRef = useRef(null);

  useEffect(() => {
    amountRef.current?.focus();
  }, []);

  const parsed = Number(amount);
  // A payment cannot have been collected in the future, and a typo of 2206 for 2026
  // would otherwise be recorded without comment.
  const notInTheFuture = paymentDate && paymentDate <= today();
  const valid =
    method &&
    notInTheFuture &&
    amount !== "" &&
    Number.isFinite(parsed) &&
    parsed > 0;

  const submit = async () => {
    if (!valid || posting) return;
    setPosting(true);
    setError(null);
    try {
      const resp = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/${claim.id}/patient-payment`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount,
            method,
            payment_date: paymentDate,
            reference_number: reference || undefined,
          }),
        },
      );
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(
          body.error || `Could not record the payment (${resp.status})`,
        );
      }
      onPosted(body);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="posting-modal-backdrop" onClick={onClose}>
      <div
        className="posting-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Record a patient payment against this claim"
      >
        <div className="posting-modal-header">
          <h2>Record a patient payment</h2>
          <button
            className="posting-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="posting-modal-body">
          <div className="posting-claim-summary">
            <span>
              Claim <strong>#{claim.id}</strong>
            </span>
            <span>{claim.patient_name || "—"}</span>
            <span>{claim.date_of_service || "—"}</span>
            <span>
              Billed <strong>{formatCurrency(claim.billed_amount)}</strong>
            </span>
          </div>

          <p className="posting-modal-lede">
            This records money the patient has <strong>already paid</strong> and
            applies it to this claim. Atlas does not charge a card from here.
          </p>

          <label className="posting-field">
            <span>Amount</span>
            <input
              ref={amountRef}
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              placeholder="0.00"
              disabled={posting}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>

          <label className="posting-field">
            <span>Method</span>
            <select
              value={method}
              disabled={posting}
              onChange={(e) => setMethod(e.target.value)}
            >
              <option value="">How did they pay?</option>
              {METHODS.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>

          <label className="posting-field">
            <span>Date received</span>
            <input
              type="date"
              min={DATE_INPUT_MIN}
              max={today()}
              value={paymentDate}
              disabled={posting}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </label>

          <label className="posting-field">
            <span>
              Reference <em>(optional)</em>
            </span>
            <input
              type="text"
              value={reference}
              maxLength={50}
              placeholder="check number, receipt id"
              disabled={posting}
              onChange={(e) => setReference(e.target.value)}
            />
          </label>

          {error && <div className="posting-error">{error}</div>}
        </div>

        <div className="posting-modal-footer">
          <button
            className="btn btn-secondary btn-sm"
            onClick={onClose}
            disabled={posting}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={submit}
            disabled={!valid || posting}
          >
            {posting
              ? "Recording…"
              : valid
                ? `Record ${formatCurrency(parsed)}`
                : "Record payment"}
          </button>
        </div>
      </div>
    </div>
  );
}
